import { Column } from 'primereact/column'
import { useTranslation } from 'react-i18next'
import Button from '../../../components/ui/Button'
import DataTable from '../../../components/ui/DataTable'
import { formatDateTime } from '../../../lib/datetime'
import ReadSwitch from './ReadSwitch'
import { isClickable } from '../hooks/useNotificationNavigate'
import type { Notification } from '../types'

/**
 * Bildirim listesi tablosu (spec FR-025) — beş kolon: başlık · detay · tarih-saat · okundu
 * anahtarı · işlemler.
 *
 * <p>Paylaşılan {@code DataTable} kullanılır (Reuse Mandate). Kolon sıralaması **kapalıdır**: düzen
 * sunucuda sabittir (`plannedSendingTime` azalan) ve uç `sort` parametresini yok sayar.
 *
 * <p>Okunmamış satırlar `notif-row-unread` ile kalın gösterilir (spec FR-013).
 *
 * <p><b>Satırın kendisi tıklanmaz</b> (058 revizyonu): hedefe gitmek için "Detaya git" düğmesi
 * kullanılır. Satır tıklaması, içindeki okundu anahtarıyla aynı alanı paylaştığından "okundu
 * yapayım derken sayfa değiştirme" kazasına yol açıyordu; ayrıca hedefsiz satırlarda tıklamanın
 * sessizce hiçbir şey yapmaması kullanıcıya bozukmuş gibi görünüyordu. Artık gidilebilen satırda
 * düğme **görünür**, gidilemeyende hiç çizilmez (spec FR-020.5).
 */
export interface NotificationsTableProps {
  items: Notification[]
  loading: boolean
  totalRecords: number
  first: number
  rows: number
  onPage: (event: { first: number; rows: number }) => void
  onOpen: (item: Notification) => void
  emptyMessage: React.ReactNode
}

export default function NotificationsTable({
  items,
  loading,
  totalRecords,
  first,
  rows,
  onPage,
  onOpen,
  emptyMessage,
}: Readonly<NotificationsTableProps>) {
  const { t } = useTranslation()

  return (
    <DataTable
      value={items}
      loading={loading}
      lazy
      paginator
      first={first}
      rows={rows}
      totalRecords={totalRecords}
      onPage={(e) => onPage({ first: e.first, rows: e.rows })}
      emptyMessage={emptyMessage}
      rowClassName={(row: Notification) => (row.read ? '' : 'notif-row-unread')}
    >
      <Column
        field="subject"
        header={t('notifications.columns.subject')}
        // Her iki dilde de başlık boşsa yedek etiket (spec FR-014).
        body={(row: Notification) => row.subject ?? t('notifications.untitled')}
        // Tablonun ~dörtte biri: detay kolonu ortaya yakın başlar ve kalan geniş alanı alır.
        style={{ width: '26%' }}
      />
      <Column
        field="content"
        header={t('notifications.columns.content')}
        // Boş hücrede "-"/"—" YAZILMAZ; boş bırakılır (anayasa: Liste → Boş hücre gösterimi).
        // En çok iki satır; tamamı üzerine gelince ipucunda.
        body={(row: Notification) => (
          <span className="notif-content-text" title={row.content ?? undefined}>
            {row.content ?? ''}
          </span>
        )}
        className="notif-col-content"
      />
      <Column
        field="plannedSendingTime"
        header={t('notifications.columns.date')}
        body={(row: Notification) => formatDateTime(row.plannedSendingTime)}
        style={{ width: '11rem' }}
      />
      <Column
        field="read"
        header={t('notifications.columns.status')}
        // Anahtar iki yöne de çalışır (spec FR-017a); yanındaki metin durumu renkten bağımsız söyler.
        body={(row: Notification) => (
          <div className="notif-status-cell">
            <ReadSwitch item={row} />
            <span className="notif-status-label">
              {row.read ? t('notifications.status.read') : t('notifications.status.unread')}
            </span>
          </div>
        )}
        style={{ width: '10rem' }}
      />
      <Column
        header={t('notifications.columns.actions')}
        className="notif-col-actions"
        style={{ width: '7rem' }}
        // `null` döner — `false` DEĞİL: PrimeReact hücre içeriğini JSX'e çevirirken boolean'ı
        // metne dönüştürüp hücreye "false" yazıyor (React'in kendi davranışı değil).
        body={(row: Notification) =>
          isClickable(row.targetType) ? (
            <Button
              icon="pi pi-arrow-right"
              rounded
              text
              aria-label={t('notifications.openDetail')}
              tooltip={t('notifications.openDetail')}
              tooltipOptions={{ position: 'bottom' }}
              onClick={() => onOpen(row)}
            />
          ) : null
        }
      />
    </DataTable>
  )
}
